<script setup lang="ts">
  import { computed, ref, watch, onMounted, nextTick, type ComponentPublicInstance } from "vue";
  import { storeToRefs } from "pinia";
  import { ArrowDown, ArrowUp } from "lucide-vue-next";
  import {
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
  } from "@/components/ui/table";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useCampaignsPivotStore, type MetricsTableData, type MetricsTableRow } from "@stores/useCampaignsPivotStore";
  import { dimensionImage, dimensionLabel, dimensionValue } from "../pivotDimensions";
  import DimensionThumbnail from "./DimensionThumbnail.vue";
  import { PIVOT_METRICS, formatMetricValue, metricCellClass, type PivotMetricDefinition } from "../pivotMetrics";

  // `only` restricts the table to some metrics (split layout: one table per metric).
  const props = defineProps<{ data: MetricsTableData; only?: string[] }>();

  const { selectedMetrics } = storeToRefs(useCampaignsPivotStore());

  const dimColumns = computed(() => props.data.rowDimensions.map((d) => ({ key: d, label: dimensionLabel(d) })));

  // Surveys / N per survey read 1 / N on a single survey — hidden there.
  const visibleMetrics = computed(() =>
    PIVOT_METRICS.filter(
      (m) =>
        selectedMetrics.value.includes(m.key) &&
        (!props.only || props.only.includes(m.key)) &&
        (props.data.multiSurvey || !m.multiSurvey),
    ),
  );

  // Horizontal layout: one anonymous column when the table is flat, otherwise
  // one per column bucket plus a trailing row total.
  interface TableColumn {
    key: string;
    label: string | null;
    isTotal?: boolean;
  }

  const tableColumns = computed<TableColumn[]>(() => {
    if (!props.data.columns.length) return [{ key: "__all__", label: null }];
    return [
      ...props.data.columns.map((c) => ({
        key: c.key,
        label: props.data.columnDimensions.map((d) => dimensionValue(d, c.dims)).join(" · "),
      })),
      { key: "__row_total__", label: "Total", isTotal: true },
    ];
  });

  const hasColumnBuckets = computed(() => props.data.columns.length > 0);

  function cellMetrics(row: MetricsTableRow, column: TableColumn) {
    if (!hasColumnBuckets.value || column.isTotal) return row.metrics;
    return row.cells[column.key] ?? {};
  }

  const groupSpans = computed(() => {
    const spans: Array<{ label: string; count: number }> = [];
    for (const m of visibleMetrics.value) {
      const last = spans[spans.length - 1];
      if (last?.label === m.group) last.count++;
      else spans.push({ label: m.group, count: 1 });
    }
    return spans;
  });

  function isGroupBoundary(index: number) {
    return index > 0 && visibleMetrics.value[index].group !== visibleMetrics.value[index - 1].group;
  }

  // Metric cells always carry border-l; this decides whether it reads as a
  // column-bucket separator, a metric-group boundary, or nothing.
  function metricBorderClass(metricIndex: number) {
    if (metricIndex === 0) return "";
    if (hasColumnBuckets.value) return "border-transparent";
    return isGroupBoundary(metricIndex) ? "border-dashed" : "border-transparent";
  }

  // ── Sticky dimension columns: offsets follow the rendered header widths ───

  const dimHeaderEls = ref<HTMLElement[]>([]);
  const dimLeftOffsets = ref<number[]>([]);

  function setDimHeaderRef(el: Element | ComponentPublicInstance | null, i: number) {
    const node = el && "$el" in el ? (el.$el as HTMLElement) : (el as HTMLElement | null);
    if (node) dimHeaderEls.value[i] = node;
  }

  function recomputeOffsets() {
    const offsets: number[] = [];
    let acc = 0;
    for (const el of dimHeaderEls.value) {
      offsets.push(acc);
      acc += el?.offsetWidth ?? 0;
    }
    dimLeftOffsets.value = offsets;
  }

  onMounted(() => nextTick(recomputeOffsets));
  watch(() => props.data, () => nextTick(recomputeOffsets));

  const isLastDim = (index: number) => index === dimColumns.value.length - 1;

  function dimStyle(index: number, zIndex: number) {
    return {
      position: "sticky" as const,
      left: `${dimLeftOffsets.value[index] ?? 0}px`,
      zIndex,
      background: "white",
      whiteSpace: "nowrap" as const,
      boxShadow: isLastDim(index) ? "2px 0 4px -1px rgba(0,0,0,0.08)" : undefined,
    };
  }

  const value = (row: MetricsTableRow, column: TableColumn, metric: PivotMetricDefinition) =>
    cellMetrics(row, column)[metric.key as keyof typeof row.metrics] ?? null;

  // ── Sorting: click a metric header; the "?" and TOTAL rows stay at the bottom ──

  interface SortState {
    columnKey: string;
    metricKey: string;
    direction: "asc" | "desc";
  }

  const sort = ref<SortState | null>(null);

  const isSortedBy = (column: TableColumn, metric: PivotMetricDefinition) =>
    sort.value?.columnKey === column.key && sort.value?.metricKey === metric.key;

  function toggleSort(column: TableColumn, metric: PivotMetricDefinition) {
    if (!isSortedBy(column, metric)) sort.value = { columnKey: column.key, metricKey: metric.key, direction: "desc" };
    else if (sort.value?.direction === "desc") sort.value = { ...sort.value, direction: "asc" };
    else sort.value = null;
  }

  const rows = computed(() => {
    const current = sort.value;
    if (!current) return props.data.rows;
    const column = tableColumns.value.find((c) => c.key === current.columnKey);
    const metric = visibleMetrics.value.find((m) => m.key === current.metricKey);
    if (!column || !metric) return props.data.rows;

    const sign = current.direction === "asc" ? 1 : -1;
    const sorted = props.data.rows
      .filter((r) => !r.isSpecial)
      .sort((a, b) => {
        const va = value(a, column, metric);
        const vb = value(b, column, metric);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        return (va - vb) * sign;
      });
    return [...sorted, ...props.data.rows.filter((r) => r.isSpecial)];
  });

  const totalCols = computed(() => dimColumns.value.length + tableColumns.value.length * visibleMetrics.value.length);
</script>

<template>
  <div
    class="rounded-md border bg-white overflow-auto [&_td]:text-xs [&_th]:text-xs"
    :class="only ? '' : 'max-h-[calc(100vh-110px)]'">
    <Table>
      <TableHeader>
        <TableRow class="border-b-0">
          <TableHead
            v-for="(dim, i) in dimColumns"
            :key="`gs-${dim.key}`"
            :style="dimStyle(i, 3)"
            class="border-b-0" />
          <template v-if="hasColumnBuckets">
            <TableHead
              v-for="col in tableColumns"
              :key="`cg-${col.key}`"
              :colspan="visibleMetrics.length"
              class="text-center text-xs font-semibold border-b-0 border-l bg-muted/30 whitespace-nowrap"
              :class="col.isTotal ? 'uppercase tracking-wide' : ''">
              {{ col.label }}
            </TableHead>
          </template>
          <template v-else>
            <TableHead
              v-for="(span, si) in groupSpans"
              :key="span.label"
              :colspan="span.count"
              class="text-center text-xs font-semibold uppercase tracking-wide border-b-0 border-l bg-muted/30"
              :class="si > 0 ? 'border-dashed' : ''">
              {{ span.label }}
            </TableHead>
          </template>
        </TableRow>
        <TableRow>
          <TableHead
            v-for="(dim, i) in dimColumns"
            :key="dim.key"
            :ref="(el) => setDimHeaderRef(el, i)"
            :style="dimStyle(i, 3)">
            {{ dim.label }}
          </TableHead>
          <template
            v-for="col in tableColumns"
            :key="`mh-${col.key}`">
            <TableHead
              v-for="(metric, mi) in visibleMetrics"
              :key="`${col.key}-${metric.key}`"
              class="text-right min-w-20 border-l cursor-pointer select-none group/head"
              :class="metricBorderClass(mi)"
              @click="toggleSort(col, metric)">
              <span class="inline-flex items-center justify-end gap-0.5">
                <component
                  :is="sort?.direction === 'asc' && isSortedBy(col, metric) ? ArrowUp : ArrowDown"
                  class="size-3 shrink-0 transition-opacity"
                  :class="isSortedBy(col, metric) ? 'opacity-100' : 'opacity-0 group-hover/head:opacity-40'" />
                <Tooltip
                  :content="`${metric.label} — ${metric.formula}`"
                  side="top">
                  <span>{{ metric.label }}</span>
                </Tooltip>
              </span>
            </TableHead>
          </template>
        </TableRow>
      </TableHeader>

      <TableBody>
        <TableEmpty
          v-if="!visibleMetrics.length"
          :colspan="dimColumns.length || 1">
          Pick at least one metric in Columns
        </TableEmpty>
        <TableRow
          v-for="row in visibleMetrics.length ? rows : []"
          :key="row.key"
          :class="{
            'border-t-2 font-semibold': row.isSpecial === 'total',
            'text-muted-foreground italic': row.isSpecial === 'unattributed',
          }">
          <TableCell
            v-for="(dim, i) in dimColumns"
            :key="dim.key"
            :class="row.isSpecial ? '' : 'font-medium'"
            :style="dimStyle(i, 1)">
            <template v-if="row.isSpecial === 'total' && i === 0">TOTAL</template>
            <template v-else-if="row.isSpecial === 'unattributed' && i === 0">?</template>
            <template v-else-if="row.isSpecial">—</template>
            <DimensionThumbnail
              v-else-if="dimensionImage(dim.key, row.dims)"
              :image="dimensionImage(dim.key, row.dims)!"
              :label="dimensionValue(dim.key, row.dims)" />
            <template v-else>{{ dimensionValue(dim.key, row.dims) }}</template>
          </TableCell>

          <template
            v-for="col in tableColumns"
            :key="`${row.key}-${col.key}`">
            <TableCell
              v-for="(metric, mi) in visibleMetrics"
              :key="`${col.key}-${metric.key}`"
              class="text-right tabular-nums border-l"
              :class="[metricCellClass(metric, value(row, col, metric)), metricBorderClass(mi)]">
              {{ formatMetricValue(metric, value(row, col, metric)) }}
            </TableCell>
          </template>
        </TableRow>

        <TableEmpty
          v-if="visibleMetrics.length && rows.filter((r) => !r.isSpecial).length === 0"
          :colspan="totalCols">
          No data for this period
        </TableEmpty>
      </TableBody>
    </Table>
  </div>
</template>
